<template>
  <v-container>
    <v-card elevation="2" class="pa-4">
      <v-card-title class="text-h6 font-weight-bold">Inventory Feeds</v-card-title>
      <v-divider class="mb-4" />

      <v-data-table :headers="headers" :items="sources" density="compact" :loading="isLoading">
        <!-- format the lastModified timestamp -->
        <template #item.lastProcessedTime="{ item }">
          <template v-if="item.hasData">
            <DateTimeWithDealerTooltip :value="item.lastProcessedTime" :text="prettyDate(item.lastProcessedTime)" />
          </template>
          <template v-else>
            <span class="text-grey">Not processed yet</span>
          </template>
        </template>

        <!-- clickable "Added" count -->
        <template #item.counts.added="{ item }">
          <v-btn
            v-if="item.hasData && item.counts.added > 0"
            variant="text"
            class="pa-0"
            @click="openVinDialog(item.vins.added)"
          >
            {{ item.counts.added }}
          </v-btn>
          <span v-else class="text-grey">{{ item.counts.added }}</span>
        </template>

        <!-- clickable "Removed" count -->
        <template #item.counts.removed="{ item }">
          <v-btn
            v-if="item.hasData && item.counts.removed > 0"
            variant="text"
            class="pa-0"
            @click="openVinDialog(item.vins.removed)"
          >
            {{ item.counts.removed }}
          </v-btn>
          <span v-else class="text-grey">{{ item.counts.removed }}</span>
        </template>

        <!-- clickable "Changed" count -->
        <template #item.counts.changed="{ item }">
          <v-btn
            v-if="item.hasData && item.counts.changed > 0"
            variant="text"
            class="pa-0"
            @click="openVinDialog(item.vins.changed)"
          >
            {{ item.counts.changed }}
          </v-btn>
          <span v-else class="text-grey">{{ item.counts.changed }}</span>
        </template>

        <!-- download button -->
        <template #item.actions="{ item }">
          <v-btn v-if="item.hasData" class="ma-2" variant="text" @click="downloadFeed(item)">
            Download Raw File
          </v-btn>
          <span v-else class="text-grey text-caption">No data available</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- VIN list dialog -->
    <v-dialog v-model="showVinDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span>VINs</span>
          <v-spacer />
          <v-text-field
            v-model="vinSearch"
            dense
            hide-details
            placeholder="Search VIN"
            append-icon="mdi-magnify"
            class="ml-4"
          />
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 400px; overflow-y: auto">
          <v-list dense>
            <v-list-item v-for="vin in filteredVins" :key="vin" class="pa-0 ma-0">
              <v-list-item-title>{{ vin }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!filteredVins.length">
              <v-list-item-title class="text--disabled"> No matching VINs </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showVinDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { getSourcesProcessDetails, getSourceStats } from '@/services/feedSourceService'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useSourcesStore } from '@/stores/sourcesStore'
import { prettyDate } from '@/utils'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'

const accountStore = useAccountSettingsStore()
const sourcesStore = useSourcesStore()
const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL

const sources = ref<any[]>([])
const showVinDialog = ref(false)
const selectedVins = ref<string[]>([])
const vinSearch = ref('')
const isLoading = ref(true)

const headers = [
  { title: 'Source', value: 'feedSource' },
  { title: 'Last Processed', value: 'lastProcessedTime' },
  { title: 'Added', value: 'counts.added', align: 'center' as const },
  { title: 'Removed', value: 'counts.removed', align: 'center' as const },
  { title: 'Changed', value: 'counts.changed', align: 'center' as const },
  { title: 'Actions', value: 'actions', sortable: false, align: 'center' as const },
]

function openVinDialog(vins: string[]) {
  selectedVins.value = vins || []
  showVinDialog.value = true
}

// compute filtered list
const filteredVins = computed(() => {
  const term = vinSearch.value.trim().toLowerCase()
  if (!term) return selectedVins.value
  return selectedVins.value.filter((v) => v.toLowerCase().includes(term))
})

const initData = async (url: string, accountId: string) => {
  // Use cached available dealer sources from store (fetched on app load)
  const availableSources = sourcesStore.sources
  console.log('##### availableSources (from cache)', availableSources)

  // Fetch processing details
  const processingDetails = await getSourcesProcessDetails(url, accountId)
  console.log('##### processingDetails', processingDetails)

  // Create a map of processing data by importProductId
  const processingMap = new Map()
  processingDetails.forEach((detail: any) => {
    if (detail.importProductId) {
      processingMap.set(detail.importProductId, detail)
    }
  })

  // Merge: for each available source, check if it has processing data
  sources.value = availableSources
    .filter((source) => source.type === 'feed') // Only show feeds
    .map((source) => {
      const importProductId = source.id // e.g., 'import_homenet'
      const processingData = processingMap.get(importProductId)

      if (processingData) {
        // Source has been processed - use real data
        return {
          importProductId: processingData.importProductId,
          feedSource: source.friendlyName || processingData.reportData?.feedSource || source.id,
          lastProcessedTime: processingData.lastProcessedTime,
          counts: processingData.reportData?.counts || { added: 0, removed: 0, changed: 0 },
          vins: {
            added: processingData.reportData?.added || [],
            removed: processingData.reportData?.removed || [],
            changed: processingData.reportData?.changed || [],
          },
          hasData: true,
          latestReportUrl: processingData.latestReportUrl,
        }
      } else {
        // Source exists but hasn't been processed yet
        return {
          importProductId: source.id,
          feedSource: source.friendlyName || source.id.replace('import_', ''),
          lastProcessedTime: null,
          counts: { added: 0, removed: 0, changed: 0 },
          vins: { added: [], removed: [], changed: [] },
          hasData: false,
        }
      }
    })

  isLoading.value = false
}

onMounted(() => {
  initData(iseEndpoint, accountStore.accountId)
})

async function downloadFeed(item: any) {
  if (!item.hasData) {
    console.log('No data available for this source yet')
    return
  }

  try {
    console.log({
      iseEndpoint,
      account: accountStore.accountId,
      product: item.importProductId,
    })
    const freshData = await getSourceStats(
      iseEndpoint,
      accountStore.accountId,
      item.importProductId,
    )

    if (!freshData?.presignedUrl) {
      console.log('No URL returned')
      return
    }

    // kick off browser download
    const a = document.createElement('a')
    a.href = freshData.presignedUrl
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (err) {
    console.error(err)
  }
}
</script>
